import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Mail, Phone, MapPin, Hash, Lock, Eye, EyeOff, User, BadgeCheck,
  Home, FileText, Images, Receipt, ArrowRight, ArrowLeft, Loader2, RotateCcw,
  Check, ShieldCheck, Stethoscope,
} from "lucide-react";
import BrandPanel from "@/components/auth/BrandPanel";
import webLogo from "@/assets/images/web_logo.png";
import Field from "@/components/auth/Field";
import SelectField from "@/components/auth/SelectField";
import Alert from "@/components/auth/Alert";
import OtpInput from "@/components/auth/OtpInput";
import FileUpload from "@/components/auth/FileUpload";
import { State, City } from "country-state-city";
import { getStrength } from "@/lib/passwordStrength";

import appConfig from "@/config/appConfig";

const API_BASE = appConfig.apiBase;

async function fetchJson(url, options = {}) {
  try {
    const res = await fetch(url, options);
    const text = await res.text();
    let data;

    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }

    if (!res.ok) {
      return {
        ok: false,
        message: data?.message || res.statusText || `Request failed with status ${res.status}`,
        data,
      };
    }

    return { ok: true, data };
  } catch (error) {
    return {
      ok: false,
      message: error?.message || "Network request failed.",
      data: null,
    };
  }
}

async function apiPostJson(path, body) {
  return fetchJson(`${API_BASE}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

async function apiPostQuery(path, params) {
  const query = new URLSearchParams(params).toString();
  return fetchJson(`${API_BASE}/${path}?${query}`, {
    method: "POST",
  });
}

async function apiCheckSignupDuplicates({ clinicName, email, contactNumber }) {
  return apiPostJson("check-signup-duplicates", { clinicName, email, contactNumber });
}

async function apiRequestSignupOtp({ email }) {
  return apiPostQuery("request-signup-otp", { email });
}

async function apiVerifySignupOtp({ email, otp }) {
  const res = await apiPostQuery("verify-signup-otp", { email, otp });
  if (!res.ok) return { ok: false, message: res.message, valid: false };
  const success = res.data?.success ?? true;
  return { ok: true, valid: Boolean(success), message: res.data?.message };
}

async function apiSignup(formData) {
  return fetchJson(`${API_BASE}/signup`, {
    method: "POST",
    body: formData,
  });
}

const INDIA_STATES = State.getStatesOfCountry("IN");

const stepMeta = [
  { key: 1, label: "Clinic profile", icon: Building2 },
  { key: 2, label: "Verify email", icon: ShieldCheck },
  { key: 3, label: "Upload documents", icon: FileText },
];

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

function formatMMSS(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const r = (s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}

export default function ClinicSignup() {
  const navigate = useNavigate();

  // Step 1 — clinic profile
  const [clinicName, setClinicName] = useState("");
  const [email, setEmail] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [stateName, setStateName] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // Step 3 — clinic docs
  const [doctorName, setDoctorName] = useState("");
  const [doctorRegistrationNumber, setDoctorRegistrationNumber] = useState("");
  const [clinicAddress, setClinicAddress] = useState("");
  const [clinicRegistrationCertificate, setClinicRegistrationCertificate] = useState(null);
  const [doctorRegistrationCertificate, setDoctorRegistrationCertificate] = useState(null);
  const [clinicPhotos, setClinicPhotos] = useState([]);
  const [gstCertificate, setGstCertificate] = useState(null);

  // OTP
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpStep, setOtpStep] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpStatus, setOtpStatus] = useState("idle");

  // meta
  const [step, setStep] = useState(1); // 1: profile → 2: otp → 3: docs
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [clinicCodeValue, setClinicCodeValue] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [duplicates, setDuplicates] = useState({});
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  const removeFieldError = (field, errors = {}) => {
    const next = { ...errors };
    delete next[field];
    return next;
  };

  const duplicateErrorMessage = (field) => {
    switch (field) {
      case "clinicName":
        return "This clinic name is already in use. Try logging in or enter a different clinic name.";
      case "email":
        return "This email is already registered. Try logging in or use a different email.";
      case "contactNumber":
        return "This phone number is already registered. Try logging in or use a different number.";
      default:
        return "This value is already registered. Try logging in or enter different details.";
    }
  };

  const cityOptions = useMemo(() => (
    stateCode ? City.getCitiesOfState("IN", stateCode).map((c) => ({ value: c.name, label: c.name })) : []
  ), [stateCode]);

  // OTP timer
  useEffect(() => {
    if (!otpSent || otpTimer <= 0) return;
    const t = setInterval(() => setOtpTimer((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [otpSent, otpTimer]);

  const strength = getStrength(password);

  const handleStateChange = (e) => {
    const code = e.target.value;
    setStateCode(code);
    const found = INDIA_STATES.find((s) => s.isoCode === code);
    setStateName(found?.name || "");
    setCity("");
  };

  const validateProfile = () => {
    const errs = {};
    const currentDuplicates = duplicates || {};

    if (!clinicName.trim()) errs.clinicName = "Clinic name is required.";
    if (!/^\S+@\S+\.\S+$/.test(email)) errs.email = "Enter a valid email address.";
    if (!/^\d{10}$/.test(contactNumber)) errs.contactNumber = "Enter a 10-digit phone number.";
    if (!stateCode) errs.stateCode = "Select a state.";
    if (!city) errs.city = "Select a city.";
    if (!/^\d{6}$/.test(pincode)) errs.pincode = "Enter a 6-digit pincode.";
    if (password.length < 8) errs.password = "Minimum 8 characters.";
    if (password !== confirmPassword) errs.confirmPassword = "Passwords do not match.";
    if (!agreeTerms) errs.agreeTerms = "You must accept the terms.";
    if (currentDuplicates.email) errs.email = duplicateErrorMessage("email");
    if (currentDuplicates.contactNumber) errs.contactNumber = duplicateErrorMessage("contactNumber");
    if (currentDuplicates.clinicName) errs.clinicName = duplicateErrorMessage("clinicName");
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateDocs = () => {
    const errs = {};
    if (!doctorName.trim()) errs.doctorName = "Doctor name is required.";
    if (!doctorRegistrationNumber.trim()) errs.doctorRegistrationNumber = "Registration number is required.";
    if (!clinicAddress.trim()) errs.clinicAddress = "Clinic address is required.";
    if (!doctorRegistrationCertificate) errs.doctorRegistrationCertificate = "Doctor certificate is required.";
    if (!clinicPhotos || clinicPhotos.length !== 3) errs.clinicPhotos = "Please upload exactly 3 clinic photos.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const debouncedDuplicateCheck = async () => {
    // Only check if user has entered at least one of the fields
    if (!clinicName && !email && !contactNumber) {
      setDuplicates({});
      setFieldErrors((prev) => removeFieldError("clinicName", removeFieldError("email", removeFieldError("contactNumber", prev))));
      return;
    }
    try {
      setCheckingDuplicates(true);
      const res = await apiCheckSignupDuplicates({ clinicName, email, contactNumber });
      // Backend returns { success: false, field: '<field>', message: '...' } when a conflict is found
      if (res.ok && res.data) {
        if (res.data.success === false && res.data.field) {
          setDuplicates({ [res.data.field]: true });
          setFieldErrors((prev) => ({
            ...removeFieldError(res.data.field, prev),
            [res.data.field]: duplicateErrorMessage(res.data.field),
          }));
        } else {
          setDuplicates({});
          setFieldErrors((prev) => removeFieldError("clinicName", removeFieldError("email", removeFieldError("contactNumber", prev))));
        }
      } else {
        setDuplicates({});
        setFieldErrors((prev) => removeFieldError("clinicName", removeFieldError("email", removeFieldError("contactNumber", prev))));
      }
    } catch (err) {
      setDuplicates({});
      setFieldErrors((prev) => removeFieldError("clinicName", removeFieldError("email", removeFieldError("contactNumber", prev))));
    } finally {
      setCheckingDuplicates(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(debouncedDuplicateCheck, 600);
    return () => clearTimeout(t);
  }, [clinicName, email, contactNumber]);

  const requestOtp = async () => {
    setError(""); setSuccess("");
    if (!validateProfile()) { setError("Please fix the highlighted fields."); return; }
    setLoading(true);
    const res = await apiRequestSignupOtp({ email });
    setLoading(false);
    if (!res.ok) return setError(res.message);
    setOtpSent(true);
    setOtpTimer(300);
    setOtpStep(true);
    setOtp(["", "", "", "", "", ""]);
    setOtpVerified(false);
    setOtpStatus("idle");
    setStep(2);
    setSuccess(res.message);
  };

  const validateOtpLive = async (arr) => {
    const s = arr.join("");
    if (s.length !== 6) { setOtpStatus("idle"); setOtpVerified(false); return; }
    setOtpStatus("pending");
    const res = await apiVerifySignupOtp({ email, otp: s });
    if (res.valid) {
      setOtpVerified(true);
      setOtpStatus("valid");
    } else {
      setOtpVerified(false);
      setOtpStatus("invalid");
    }
  };
  const handleOtpChange = (i, v) => {
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (next.join("").length === 6) validateOtpLive(next);
    else { setOtpStatus("idle"); setOtpVerified(false); }
  };
  const handlePaste = (e) => {
    const t = (e.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "");
    if (t.length === 6) {
      e.preventDefault();
      const arr = t.split("");
      setOtp(arr);
      validateOtpLive(arr);
    }
  };

  const verifyOtpAndSignup = async () => {
    setError(""); setSuccess("");
    if (!otpVerified) return setError("Verify your email OTP first.");
    if (!validateDocs()) { setError("Please provide all required documents."); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append("clinicName", clinicName);
    fd.append("email", email);
    fd.append("contactNumber", contactNumber);
    fd.append("city", city);
    fd.append("stateName", stateName);
    fd.append("stateCode", stateCode);
    fd.append("pincode", pincode);
    fd.append("password", password);
    fd.append("doctorName", doctorName);
    fd.append("doctorRegistrationNumber", doctorRegistrationNumber);
    fd.append("clinicAddress", clinicAddress);
    fd.append("otp", otp.join(""));
    if (clinicRegistrationCertificate) fd.append("clinicRegistrationCertificate", clinicRegistrationCertificate);
    fd.append("doctorRegistrationCertificate", doctorRegistrationCertificate);
    clinicPhotos.forEach((p) => fd.append("clinicPhotos", p));
    if (gstCertificate) fd.append("gstCertificate", gstCertificate);

    try {
      const res = await apiSignup(fd);
      if (!res.ok) {
        return setError(res.message || "Signup failed. Please try again.");
      }

      const code = res.data?.clinicCode || null;
      setClinicCodeValue(code);
      setSuccess(`Clinic registered successfully.`);
      // show modal with clinic code and auto-close
      setShowSuccessModal(true);
    } catch (err) {
      setError(err?.message || "Signup request failed. Please check your network and try again.");
    } finally {
      setLoading(false);
    }
  };

    // Auto-close success modal after 15s and redirect to sign-in
    useEffect(() => {
      if (!showSuccessModal) return undefined;
      const t = setTimeout(() => {
        setShowSuccessModal(false);
        navigate("/signin");
      }, 15000);
      return () => clearTimeout(t);
    }, [showSuccessModal, navigate]);

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] bg-form-panel">
      <div className="hidden lg:block">
        <BrandPanel signup />
      </div>

      {/* Mobile mini-brand */}
      <div className="relative flex items-center justify-between px-4 sm:px-6 py-2 sm:py-3 lg:hidden bg-[radial-gradient(circle_at_top_left,_rgba(255,215,68,0.16),_transparent_36%),linear-gradient(180deg,_#020406_0%,_#0f172a_45%,_#081024_100%)] text-white">
        <div className="flex items-center gap-3">
          <div className="w-0 sm:w-2 md:w-4 lg:w-6" />
          <img src={webLogo} alt="CliniQueue" className="h-16 sm:h-20 md:h-24 lg:h-28 w-auto -ml-2 sm:-ml-1 md:ml-0" />
          <div className="ml-3 mt-0.5 h-px w-16 sm:w-24 md:w-32 bg-gradient-to-r from-transparent via-[rgba(56,189,248,0.75)] to-transparent opacity-90" />
        </div>
        <Link to="/signin" className="chip !py-1">
          <ArrowLeft className="h-3 w-3" /> Sign in
        </Link>
      </div>

      <div className="relative flex items-start justify-center px-5 py-8 sm:px-8 md:px-12 lg:py-14">
        <div className="w-full max-w-[620px] space-y-6">
          {/* Header + stepper */}
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[hsl(222,82%,46%)]">
              Create account
            </div>
            <h2 className="font-display text-3xl leading-[1.1] text-[hsl(220,45%,12%)] sm:text-4xl">
              {step === 1 && (<>Create your <span className="italic text-[hsl(222,82%,46%)]">clinic account</span>.</>)}
              {step === 2 && (<>Verify your <span className="italic text-[hsl(222,82%,46%)]">email</span>.</>)}
              {step === 3 && (<>Upload <span className="italic text-[hsl(222,82%,46%)]">verification docs</span>.</>)}
            </h2>
            <p className="mt-2 text-sm text-[hsl(220,15%,46%)]">
              {step === 1 && "Tell us about your clinic. This takes about 3 minutes."}
              {step === 2 && `We sent a 6-digit code to ${email}. Enter it to continue.`}
              {step === 3 && "A quick document check keeps your workspace secure and compliant."}
            </p>

            {/* Stepper */}
            <div className="mt-6 flex items-center gap-3">
              {stepMeta.map((s, i) => {
                const isActive = step === s.key;
                const isDone = step > s.key;
                const Icon = s.icon;
                return (
                  <div key={s.key} className="flex flex-1 items-center gap-3">
                    <div className={`flex items-center gap-2.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                      isActive ? "border-[hsl(222,82%,46%)] bg-[hsl(222,82%,46%)] text-white"
                      : isDone   ? "border-[hsl(152,60%,38%)] bg-[hsl(152,55%,94%)] text-[hsl(152,60%,25%)]"
                                 : "border-[hsl(220,22%,88%)] bg-white text-[hsl(220,15%,46%)]"}`}>
                      {isDone ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                      <span className="hidden sm:inline">{s.label}</span>
                      <span className="sm:hidden">Step {s.key}</span>
                    </div>
                    {i < stepMeta.length - 1 && (
                      <div className={`h-px flex-1 ${step > s.key ? "bg-[hsl(152,60%,38%)]" : "bg-[hsl(220,22%,88%)]"}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Alert type="error" onClose={() => setError("")} testId="signup-error">{error}</Alert>
          <Alert type="success" onClose={() => setSuccess("")} testId="signup-success">{success}</Alert>

          <AnimatePresence mode="wait">
            {/* STEP 1 — profile */}
            {step === 1 && (
              <motion.div key="s1" {...fadeUp} className="space-y-4">
                <Field id="clinicName" label="Clinic Name" icon={Building2}
                  value={clinicName} onChange={(e) => { setClinicName(e.target.value); setFieldErrors((prev) => removeFieldError("clinicName", prev)); setDuplicates((prev) => { const { clinicName, ...rest } = prev; return rest; }); }}
                  placeholder="e.g. Health+ Clinic"
                  error={fieldErrors.clinicName} testId="signup-clinic-name"
                  status={clinicName ? (checkingDuplicates ? 'checking' : (duplicates.clinicName ? 'taken' : 'available')) : undefined} />

                <div className="grid gap-4 sm:grid-cols-2 items-stretch">
                  <Field id="email" label="Email" icon={Mail}
                    type="email" value={email} onChange={(e) => { setEmail(e.target.value); setFieldErrors((prev) => removeFieldError("email", prev)); setDuplicates((prev) => { const { email, ...rest } = prev; return rest; }); }}
                    placeholder="you@clinic.com" error={fieldErrors.email} testId="signup-email"
                    status={email ? (checkingDuplicates ? 'checking' : (duplicates.email ? 'taken' : 'available')) : undefined} />
                  <Field id="contact" label="Contact Number" icon={Phone}
                    inputMode="numeric" maxLength={10}
                    value={contactNumber}
                    onChange={(e) => { const value = e.target.value.replace(/\D/g, "").slice(0, 10); setContactNumber(value); setFieldErrors((prev) => removeFieldError("contactNumber", prev)); setDuplicates((prev) => { const { contactNumber, ...rest } = prev; return rest; }); }}
                    placeholder="10-digit mobile" error={fieldErrors.contactNumber} testId="signup-contact"
                    status={contactNumber ? (checkingDuplicates ? 'checking' : (duplicates.contactNumber ? 'taken' : 'available')) : undefined} />
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <SelectField id="state" label="State" icon={MapPin}
                    value={stateCode} onChange={handleStateChange}
                    options={INDIA_STATES.map((s) => ({ value: s.isoCode, label: s.name }))}
                    error={fieldErrors.stateCode} testId="signup-state" />
                  <SelectField id="city" label="City"
                    value={city} onChange={(e) => setCity(e.target.value)}
                    options={cityOptions} disabled={!stateCode}
                    placeholder={stateCode ? "Select city" : "Select state first"}
                    error={fieldErrors.city} testId="signup-city" />
                  <Field id="pin" label="Pincode" icon={Hash}
                    inputMode="numeric" maxLength={6} value={pincode}
                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit" error={fieldErrors.pincode} testId="signup-pincode" />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Field id="pw" label="Password" icon={Lock}
                      type={showPw ? "text" : "password"} value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters" error={fieldErrors.password}
                      testId="signup-password"
                      suffix={
                        <button type="button" onClick={() => setShowPw(v => !v)}
                          className="rounded-md p-1.5 text-[hsl(220,15%,46%)] hover:text-[hsl(220,45%,12%)]">
                          {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      } />
                    {password && (
                      <div className="mt-2">
                        <div className="strength-bar">
                          <div className="strength-fill" style={{ width: `${(strength.score / 5) * 100}%`, background: strength.color }} />
                        </div>
                        <div className="mt-1 text-[11px] text-[hsl(220,15%,46%)]">Strength: <span className="font-semibold" style={{ color: strength.color }}>{strength.label}</span></div>
                      </div>
                    )}
                  </div>
                  <Field id="cpw" label="Confirm Password" icon={Lock}
                    type={showPw ? "text" : "password"} value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password" error={fieldErrors.confirmPassword}
                    testId="signup-confirm-password" />
                </div>

                {/* Terms checkbox */}
                <label className="mt-2 flex cursor-pointer items-start gap-3 rounded-xl border hairline bg-white/60 p-3.5 hover:border-[hsl(222,82%,46%)]">
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-[hsl(220,22%,78%)] text-[hsl(222,82%,46%)] focus:ring-[hsl(222,82%,46%)]"
                    data-testid="agree-terms-checkbox"
                  />
                  <div className="text-[13px] leading-relaxed text-[hsl(220,30%,28%)]">
                    I have read and agree to the{' '}
                    <a href="https://www.cliniqueue.in/legal/terms-of-service" target="_blank" rel="noreferrer" className="link-royal">Terms of Service</a>,{' '}
                    <a href="https://www.cliniqueue.in/legal/privacy-policy" target="_blank" rel="noreferrer" className="link-royal">Privacy Policy</a>, and the{' '}
                    <a href="https://www.cliniqueue.in/legal/data-processing-agreement" target="_blank" rel="noreferrer" className="link-royal">Data Processing Agreement</a>.
                  </div>
                </label>
                {fieldErrors.agreeTerms && (
                  <div className="text-[12px] font-medium text-[hsl(356,78%,45%)]" data-testid="agree-terms-error">
                    {fieldErrors.agreeTerms}
                  </div>
                )}

                <button onClick={requestOtp} disabled={loading}
                  className="btn-primary w-full" data-testid="signup-request-otp-btn">
                  {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending code…</>) : (<>Send verification code <ArrowRight className="h-4 w-4" /></>)}
                </button>

                <div className="text-center text-sm text-[hsl(220,30%,28%)]">
                  Already have an account?{" "}
                  <Link to="/signin" className="font-semibold text-[hsl(222,82%,46%)] hover:underline">
                    Sign in
                  </Link>
                </div>
              </motion.div>
            )}

            {/* STEP 2 — OTP */}
            {step === 2 && (
              <motion.div key="s2" {...fadeUp} className="space-y-5">
                <div className="rounded-2xl border hairline bg-white p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(220,30%,96%)] text-[hsl(222,82%,46%)]">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(220,15%,46%)]">Email sent to</div>
                      <div className="text-sm font-semibold text-[hsl(220,45%,12%)]">{email}</div>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="text-[11px] uppercase tracking-[0.14em] text-[hsl(220,15%,46%)]">Expires in</div>
                      <div className="font-mono-tab text-sm font-semibold text-[hsl(220,45%,12%)]">{formatMMSS(otpTimer)}</div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-[12px] font-semibold uppercase tracking-[0.14em] text-[hsl(220,30%,28%)]">Verification Code</label>
                  <OtpInput value={otp} onChange={handleOtpChange} onPaste={handlePaste} status={otpStatus} testIdPrefix="signup-otp" />
                  <div className="mt-3 flex items-center justify-between">
                    <button onClick={requestOtp} disabled={otpTimer > 240 || loading}
                      className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[hsl(222,82%,46%)] disabled:opacity-40"
                      data-testid="signup-resend-otp-btn">
                      <RotateCcw className="h-3.5 w-3.5" /> Resend code
                    </button>
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                  <button onClick={() => setStep(1)} className="btn-ghost" data-testid="back-to-profile-btn">
                    <ArrowLeft className="h-4 w-4" /> Back to profile
                  </button>
                  <button
                    onClick={() => { if (otpVerified) setStep(3); else setError("Please enter the correct OTP first."); }}
                    disabled={!otpVerified}
                    className="btn-primary sm:min-w-[220px]"
                    data-testid="continue-to-docs-btn"
                  >
                    Continue to documents <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* STEP 3 — docs */}
            {step === 3 && (
              <motion.div key="s3" {...fadeUp} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field id="dn" label="Doctor Name" icon={User}
                    value={doctorName} onChange={(e) => setDoctorName(e.target.value)}
                    placeholder="Dr. Full Name" error={fieldErrors.doctorName} testId="signup-doctor-name" />
                  <Field id="drn" label="Doctor Registration Number" icon={BadgeCheck}
                    value={doctorRegistrationNumber}
                    onChange={(e) => setDoctorRegistrationNumber(e.target.value)}
                    placeholder="MCI/State registration no."
                    error={fieldErrors.doctorRegistrationNumber} testId="signup-doctor-reg" />
                </div>

                <Field id="addr" label="Clinic Address" icon={Home}
                  value={clinicAddress} onChange={(e) => setClinicAddress(e.target.value)}
                  placeholder="Full street address"
                  error={fieldErrors.clinicAddress} testId="signup-clinic-address" />

                <div className="grid gap-4 sm:grid-cols-2">
                  <FileUpload
                    label="Doctor Registration Certificate"
                    hint="Required"
                    required
                    value={doctorRegistrationCertificate}
                    onChange={(f, err) => { if (err) setError(err); setDoctorRegistrationCertificate(f); }}
                    error={fieldErrors.doctorRegistrationCertificate}
                    testId="upload-doctor-cert"
                  />
                  <FileUpload
                    label="Clinic Registration Certificate"
                    hint="Optional"
                    value={clinicRegistrationCertificate}
                    onChange={(f, err) => { if (err) setError(err); setClinicRegistrationCertificate(f); }}
                    testId="upload-clinic-cert"
                  />
                </div>

                <FileUpload
                  label="Clinic Photos"
                  hint="Upload exactly 3 photos, including a front view with the clinic signboard visible"
                  required
                  multiple
                  value={clinicPhotos}
                  onChange={(f, err) => { if (err) setError(err); setClinicPhotos(f); }}
                  maxCount={3}
                  accept=".jpg,.jpeg,.png,.webp"
                  error={fieldErrors.clinicPhotos}
                  testId="upload-clinic-photos"
                />

                <FileUpload
                  label="GST Certificate"
                  hint="Optional"
                  value={gstCertificate}
                  onChange={(f, err) => { if (err) setError(err); setGstCertificate(f); }}
                  testId="upload-gst"
                />

                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                  <button onClick={() => setStep(2)} className="btn-ghost" data-testid="back-to-otp-btn">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                  <button onClick={verifyOtpAndSignup} disabled={loading} className="btn-primary sm:min-w-[240px]"
                    data-testid="signup-submit-btn">
                    {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Creating account…</>) : (<>Verify & Create Account <ArrowRight className="h-4 w-4" /></>)}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {/* Success modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative z-10 w-[min(92%,520px)] rounded-lg bg-white p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-[hsl(220,45%,12%)]">Clinic registered</h3>
            <p className="mt-2 text-sm text-[hsl(220,15%,46%)]">Your clinic has been created successfully. We've sent an email with further instructions — please check your inbox.</p>
            {clinicCodeValue && (
              <div className="mt-4 rounded-md border border-dashed border-[hsl(220,22%,88%)] bg-[hsl(220,30%,98%)] p-3">
                <div className="text-xs text-[hsl(220,15%,46%)]">Clinic code</div>
                <div className="mt-1 font-mono-tab text-lg font-semibold text-[hsl(220,45%,12%)]">{clinicCodeValue}</div>
              </div>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" className="btn-ghost" onClick={() => { setShowSuccessModal(false); navigate('/signin'); }}>Go to sign in</button>
              <button type="button" className="btn-primary" onClick={() => { setShowSuccessModal(false); navigate('/signin'); }}>Close</button>
            </div>
            <div className="mt-3 text-[11px] text-[hsl(220,15%,46%)]">This dialog will close automatically in 15 seconds.</div>
          </div>
        </div>
      )}
    </div>
  );
}
